import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { School } from 'lucide-react';

import { ConsoleIdentity } from '@/components/layout/ConsoleIdentity';
import { ConsoleLayout } from '@/components/layout/ConsoleLayout';
import { SecureImage } from '@/components/ui/SecureImage';
import { instituteApi } from '@/features/institute/api/institute.api';
import { PARENT_NAV } from '@/constants/navigation';
import { ROLE_LABEL } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { parentNotificationsApi } from '@/features/parent/api/notifications.api';
import { ChildSwitcher } from '@/features/parent/components/ChildSwitcher';
import { useParentChild } from '@/features/parent/store/child.store';

/**
 * The panel a parent reads their child's school from.
 *
 * The same frame as the institute, platform and teacher consoles — one shell,
 * one sidebar, one top bar (CLAUDE.md §3). What is different here is what the
 * rail carries: a parent's menu is grouped, because it asks two questions at
 * once, and above it sits the child switcher, because every screen behind that
 * menu is about one child and the reader has to see which.
 *
 * The bell reads the parent's own inbox under `/parent` — a teacher's notice for
 * their child's class lands there.
 */
export function ParentLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const branding = useInstituteBranding((state) => state.branding);
  const loadBranding = useInstituteBranding((state) => state.load);
  const logoVersion = useInstituteBranding((state) => state.version);
  const loadChildren = useParentChild((state) => state.load);

  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);

  // Loaded once for the whole panel rather than per screen: the switcher in the
  // rail and every page behind it read the same list, and fetching it in each
  // would let two screens disagree about which children there are.
  useEffect(() => {
    void loadChildren();
  }, [loadChildren]);

  return (
    <ConsoleLayout
      nav={PARENT_NAV}
      brand={{
        mark: branding?.hasLogo ? (
          <SecureImage
            path={instituteApi.logoPath()}
            alt=""
            reloadKey={logoVersion}
            className="h-full w-full bg-white object-contain p-0.5"
            fallback={<School className="h-[17px] w-[17px]" strokeWidth={1.9} />}
          />
        ) : (
          <School className="h-[17px] w-[17px]" strokeWidth={1.9} />
        ),
        // The school their child attends, never a name baked into the build (§4)
        // — and blank until the branding arrives rather than a stand-in word.
        title: branding?.displayName ?? '',
        subtitle: 'Parent portal',
      }}
      sidebarTop={<ChildSwitcher />}
      // No `account`: the top bar names the parent instead.
      onSignOut={async () => {
        await logout();
        navigate('/login', { replace: true });
      }}
      actions={
        <>
          <NotificationBell source={parentNotificationsApi} />
          <ConsoleIdentity
            name={user?.fullName ?? 'Parent'}
            role={user ? ROLE_LABEL[user.role] : 'Parent'}
          />
        </>
      }
    />
  );
}
